import React, { useState } from "react";
import { CheckCircle2, XCircle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export function LessonQuiz({ lesson, onScore }) {
  const [picked, setPicked] = useState({});
  const [done, setDone] = useState(false);
  const score = lesson.quiz.reduce((s, q, i) => s + (picked[i] === q.answer ? 1 : 0), 0);
  const submit = async () => {
    setDone(true);
    try { const { data } = await api.post(`/learn/lessons/${lesson.id}/quiz`, { score, total: lesson.quiz.length }); onScore?.(data.best_score); } catch {}
  };
  return (
    <div className="space-y-4" data-testid="lesson-quiz">
      {lesson.quiz.map((q, i) => (
        <div key={i} className="rounded-xl border border-white/10 p-4">
          <div className="reading text-[15px] mb-3">{i + 1}. {q.question}</div>
          <div className="grid gap-2">
            {q.options.map((o, j) => {
              const sel = picked[i] === j, right = done && j === q.answer, wrong = done && sel && j !== q.answer;
              return (
                <button key={j} data-testid={`quiz-q${i}-opt${j}`} disabled={done} onClick={() => setPicked({ ...picked, [i]: j })}
                  className={`text-left rounded-lg px-3 py-2 text-sm border transition-colors flex items-center gap-2 ${right ? "border-emerald-400/60 bg-emerald-400/10" : wrong ? "border-red-400/60 bg-red-400/10" : sel ? "border-mono-400/60 bg-mono-400/10" : "border-white/10 hover:border-white/25"}`}>
                  {right && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}{wrong && <XCircle className="w-4 h-4 text-red-400" />}{o}
                </button>
              );
            })}
          </div>
          {done && q.explanation && <div className="reading mt-2 text-xs text-slate-400">{q.explanation}</div>}
        </div>
      ))}
      {!done ? (
        <button data-testid="quiz-submit" disabled={Object.keys(picked).length < lesson.quiz.length} onClick={submit}
          className="rounded-full px-5 py-2 text-sm text-[#000000] disabled:opacity-50" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>Check answers</button>
      ) : (
        <div className="flex items-center gap-3">
          <span data-testid="quiz-score" className="text-mono-200">Score: {score} / {lesson.quiz.length}</span>
          <button data-testid="quiz-retry" onClick={() => { setPicked({}); setDone(false); }} className="text-xs text-slate-400 hover:text-white">Try again</button>
        </div>
      )}
    </div>
  );
}

export function LessonNotes({ lesson }) {
  return (
    <ul className="space-y-3" data-testid="lesson-notes">
      {lesson.revision_notes.map((n, i) => (
        <li key={i} className="reading text-[15px] flex gap-3"><span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-mono-300 shrink-0" />{n}</li>
      ))}
    </ul>
  );
}

export function LessonAsk({ lesson, cost, onAnswered }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [qa, setQa] = useState(lesson.qa || []);
  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/learn/lessons/${lesson.id}/ask`, { question });
      setQa([...qa, data.qa]); setQ(""); onAnswered?.(data.balance);
    } catch (e) { toast.error(e?.response?.status === 402 ? "Not enough credits." : "Couldn't answer — try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="lesson-ask">
      <div className="flex flex-wrap gap-2">
        {lesson.follow_ups.map((f, i) => (
          <button key={i} data-testid={`followup-${i}`} disabled={busy} onClick={() => ask(f)}
            className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:border-mono-400/50 hover:text-mono-200 text-left">{f}</button>
        ))}
      </div>
      {qa.map((x, i) => (
        <div key={i} className="space-y-1.5" data-testid="lesson-qa-item">
          <div className="text-sm text-mono-200/90">{x.question}</div>
          <div className="reading text-[15px] text-slate-200 pl-3 border-l-2 border-mono-400/30">{x.answer}</div>
        </div>
      ))}
      <div className="flex gap-2">
        <input data-testid="lesson-ask-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
          placeholder="Ask a follow-up question…" className="flex-1 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
        <button data-testid="lesson-ask-send" disabled={busy || !q.trim()} onClick={() => ask()} className="rounded-full w-10 h-10 flex items-center justify-center text-[#000000] disabled:opacity-50" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      <div className="text-[11px] text-slate-500">Each follow-up costs {cost} credit{cost === 1 ? "" : "s"}.</div>
    </div>
  );
}
